// Season scheduling: compute the active drop dates across the season and assign
// each a secret randomized timestamp inside the commissioner's local-time window.
// Parameterized by starts_on/ends_on so future custom-length seasons need no rewrite.

// How a day's drop moments are chosen.
//   "surprise"   a random moment inside the chosen window. The old and default
//                behaviour. Note this was never "any time ever": it has always
//                been a random moment inside hours the host picked.
//   "exact"      the host names the times outright.
export type TimingStyle = "surprise" | "exact";

export interface ScheduleInput {
  startsOn: string; // "YYYY-MM-DD" crew local
  endsOn: string; // "YYYY-MM-DD" crew local (inclusive)
  activeWeekdays: number[]; // 0=Sun..6=Sat
  windowStartLocal: string; // "HH:MM"
  windowEndLocal: string; // "HH:MM"
  timezone: string; // IANA tz, used by the caller to convert to UTC
  // More than one drop in a day is just this number. The old "a few a day" mode
  // was the same idea wearing a confusing name.
  dropsPerDay?: number;
  timingStyle?: TimingStyle;
  // For "exact": one time per slot, e.g. ["12:00", "20:00"]. Short lists are
  // padded by reusing the last time so a host who raises drops-per-day without
  // adding a time still gets a working schedule instead of a missing drop.
  exactTimesLocal?: string[];
  rng?: () => number;
}

export interface ScheduledDrop {
  localDate: string; // "YYYY-MM-DD"
  weekday: number;
  // Minutes-since-midnight in league local time for the secret drop moment.
  localMinuteOfDay: number;
  localTime: string; // "HH:MM"
}

function parseHm(hm: string): number {
  const [h, m] = hm.split(":").map((x) => parseInt(x, 10));
  return h * 60 + m;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// Iterate calendar dates inclusively without pulling in a date library. We treat
// the local date as a plain calendar date (no DST math here — the caller converts
// the resulting local wall-clock time to UTC via the stored IANA timezone).
export function eachDate(startsOn: string, endsOn: string): string[] {
  const dates: string[] = [];
  const [sy, sm, sd] = startsOn.split("-").map(Number);
  const [ey, em, ed] = endsOn.split("-").map(Number);
  const cur = new Date(Date.UTC(sy, sm - 1, sd));
  const end = new Date(Date.UTC(ey, em - 1, ed));
  while (cur.getTime() <= end.getTime()) {
    dates.push(
      `${cur.getUTCFullYear()}-${pad2(cur.getUTCMonth() + 1)}-${pad2(cur.getUTCDate())}`
    );
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return dates;
}

export function activeDates(input: ScheduleInput): { date: string; weekday: number }[] {
  const active = new Set(input.activeWeekdays);
  return eachDate(input.startsOn, input.endsOn)
    .map((date) => {
      const [y, m, d] = date.split("-").map(Number);
      const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
      return { date, weekday };
    })
    .filter(({ weekday }) => active.has(weekday));
}

// Share of each slice held back at both edges, so consecutive drops cannot
// crowd each other across a slice boundary. At 0.15 the guaranteed gap between
// two drops is 30% of a slice, which for three drops in an eight hour window is
// about 48 minutes.
const SLICE_EDGE_MARGIN = 0.15;

function at(date: string, weekday: number, minute: number): ScheduledDrop {
  const m = Math.max(0, Math.min(24 * 60 - 1, Math.round(minute)));
  return {
    localDate: date,
    weekday,
    localMinuteOfDay: m,
    localTime: `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`,
  };
}

// The drop moments for one active day, in order.
//
// Surprise spreads several drops across the window instead of rolling each one
// independently, because independent rolls clump: two drops in a twelve hour
// window land four minutes apart often enough to matter, and a crew that gets
// pinged twice in five minutes has effectively had one drop. The window is cut
// into equal slices and one moment is drawn inside each, which keeps every
// moment secret while guaranteeing they are spread out.
function momentsForDay(date: string, weekday: number, input: ScheduleInput): ScheduledDrop[] {
  const rng = input.rng ?? Math.random;
  const count = Math.max(1, Math.floor(input.dropsPerDay ?? 1));

  if ((input.timingStyle ?? "surprise") === "exact") {
    const times = (input.exactTimesLocal ?? []).filter(Boolean);
    if (times.length === 0) return [at(date, weekday, parseHm(input.windowStartLocal))];
    return Array.from({ length: count }, (_, i) =>
      // Reuse the last given time rather than dropping the slot entirely.
      at(date, weekday, parseHm(times[Math.min(i, times.length - 1)]))
    ).sort((a, b) => a.localMinuteOfDay - b.localMinuteOfDay);
  }

  const startMin = parseHm(input.windowStartLocal);
  const endMin = parseHm(input.windowEndLocal);
  const span = Math.max(0, endMin - startMin);
  if (span === 0) return Array.from({ length: count }, () => at(date, weekday, startMin));

  // Each drop gets its own slice of the window, and is drawn from the middle of
  // that slice rather than the whole of it. Slicing alone is not enough: one
  // moment at the very end of its slice and the next at the very start of the
  // following slice can land minutes apart, which is the clumping this is meant
  // to prevent. Holding a margin at each edge guarantees a real gap between
  // consecutive drops while keeping every moment unpredictable.
  const slice = span / count;
  const usable = slice * (1 - 2 * SLICE_EDGE_MARGIN);
  return Array.from({ length: count }, (_, i) => {
    const sliceStart = startMin + slice * i + slice * SLICE_EDGE_MARGIN;
    return at(date, weekday, sliceStart + rng() * usable);
  }).sort((a, b) => a.localMinuteOfDay - b.localMinuteOfDay);
}

// Every secret drop moment across the season, in order.
export function buildSchedule(input: ScheduleInput): ScheduledDrop[] {
  return activeDates(input).flatMap(({ date, weekday }) => momentsForDay(date, weekday, input));
}

// Season 0 helper: end date is start + 13 days (14-day inclusive window).
export function computeEndsOn(startsOn: string, lengthDays = 14): string {
  const [y, m, d] = startsOn.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + (lengthDays - 1));
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}
